import React, { useMemo, useState } from "react";
import { Box } from "@mui/system";
import { useDispatch, useSelector } from "react-redux";
import GridTable from "../../table/gridTable";
import { getBaseUrl } from "../../../client";
import {
  API_HALAL_STOCK_ALL_DIVIDENDS,
  API_HALAL_STOCK_ORDERS,
} from "../../../config/api";
import DebouncedSearchInput from "../../table/DebouncedSearchInput";
import { getAllDividends } from "../../../store/actions/stocks/getAllDividends";
import { getStockOrders } from "../../../store/actions/stocks/getStockOrders";
import Loader from "../../SharedLayouts/Loader";
import CustomButton from "../../Buttons/Button";
import ApproveOrderModal from "../../modal/ApproveOrderModal";
import RejectOrderModal from "../../modal/RejectOrderModal";
import SellStockForUserModal from "../../modal/SellStockForUserModal";
import ExportCsvTable from "../../Export/ExportCsvTable";
import { getCurrencySymbol } from "../../../Helper/getCurrencySymbol";

const statusStyle = (status) => {
  const value = (status || "").toLowerCase();
  if (value === "approved" || value === "executed" || value === "completed") {
    return { color: "#067647", backgroundColor: "#ECFDF3" };
  }
  if (value === "rejected" || value === "cancelled" || value === "failed") {
    return { color: "#B42318", backgroundColor: "#FECDCA" };
  }
  return { color: "#B54708", backgroundColor: "#FFFAEB" };
};

const orderTabs = [
  { id: "buy", name: "Buy" },
  { id: "sell", name: "Sell" },
  { id: "dividends", name: "Dividend History" },
];

function OrdersTable() {
  const dispatch = useDispatch();
  const buyOrders = useSelector((state) => state?.stocks?.buyOrders) || [];
  const sellOrders = useSelector((state) => state?.stocks?.sellOrders) || [];
  const allDividends =
    useSelector((state) => state?.stocks?.allDividends) || [];
  const buyOrdersInfo = useSelector((state) => state?.stocks?.buyOrdersInfo);
  const sellOrdersInfo = useSelector((state) => state?.stocks?.sellOrdersInfo);
  const allDividendsInfo = useSelector(
    (state) => state?.stocks?.allDividendsInfo,
  );
  const buyLoading = useSelector((state) => state?.stocks?.buyOrdersPending);
  const sellLoading = useSelector((state) => state?.stocks?.sellOrdersPending);
  const dividendsLoading = useSelector(
    (state) => state?.stocks?.allDividendsPending,
  );

  const [side, setSide] = useState("buy");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showApprove, setShowApprove] = useState(false);
  const [showReject, setShowReject] = useState(false);
  const [showSellForUser, setShowSellForUser] = useState(false);

  const isSell = side === "sell";
  const isDividend = side === "dividends";
  const orders = isDividend ? allDividends : isSell ? sellOrders : buyOrders;
  const loading = isDividend
    ? dividendsLoading
    : isSell
      ? sellLoading
      : buyLoading;

  // Server-side pagination metrics come back in the response `meta` block.
  // GridTable pages by refetching `path?page=N`, so the path has to carry the
  // same per_page and search the current view was loaded with, and rowsKey
  // tells it where the rows sit in the paged response.
  const meta = isDividend
    ? allDividendsInfo
    : isSell
      ? sellOrdersInfo
      : buyOrdersInfo;

  const pageInfo = useMemo(() => {
    if (!meta?.total) return null;
    const basePath = isDividend
      ? API_HALAL_STOCK_ALL_DIVIDENDS
      : API_HALAL_STOCK_ORDERS(side);
    const search = searchTerm
      ? `&search=${encodeURIComponent(searchTerm)}`
      : "";
    return {
      ...meta,
      data: orders,
      rowsKey: isDividend ? "dividends" : "orders",
      path: `${getBaseUrl()}api/v1/${basePath}${search}`,
    };
  }, [meta, orders, isDividend, side, searchTerm]);

  const handleSearch = (term) => {
    setSearchTerm(term);
    if (isDividend) {
      dispatch(getAllDividends(term ? { search: term } : {}));
    } else {
      dispatch(getStockOrders({ side, ...(term ? { search: term } : {}) }));
    }
  };

  const isPending = (status) => {
    const value = (status || "").toLowerCase();
    return value === "" || value === "pending" || value === "processing";
  };

  // An approved buy holding can be sold back on the user's behalf. The buy
  // order already carries the user, stock and volume needed to pre-fill the
  // sell form.
  const canSell = (row) => {
    const rowSide = (
      row?.side ||
      row?.type ||
      row?.order_type ||
      ""
    ).toLowerCase();
    const status = (row?.status || "").toLowerCase();
    return rowSide === "buy" && status === "approved";
  };

  const formatAmount = (currency, value) =>
    `${getCurrencySymbol(currency)} ${
      value || value === 0 ? Number(value).toLocaleString() : "-"
    }`;

  const baseColumns = [
    { field: "id", headerName: "Order ID", width: 90 },
    {
      field: "user",
      headerName: "User",
      width: 180,
      renderCell: (params) =>
        params.row?.user?.name ||
        params.row?.user_name ||
        params.row?.user?.email ||
        "-",
    },
    {
      field: "symbol",
      headerName: "Symbol",
      width: 120,
      renderCell: (params) =>
        params.row?.stock?.symbol || params.row?.symbol || "-",
    },
    {
      field: "side",
      headerName: "Type",
      width: 100,
      renderCell: (params) => (
        <span style={{ textTransform: "capitalize" }}>
          {params.row?.side ||
            params.row?.type ||
            params.row?.order_type ||
            "-"}
        </span>
      ),
    },
    {
      field: "order_type",
      headerName: "Order Type",
      width: 100,
      renderCell: (params) => (
        <span style={{ textTransform: "capitalize" }}>
          {params.row?.order_type || "-"}
        </span>
      ),
    },
    {
      field: "volume",
      headerName: "Volume",
      width: 110,
      renderCell: (params) => params.row?.volume ?? params.row?.quantity ?? "-",
    },
    {
      field: "price",
      headerName: "Price Per Stock",
      width: 130,
      renderCell: (params) =>
        formatAmount(params.row?.currency, params.row?.fee_breakdown?.price),
    },

    {
      field: "cost",
      headerName: "Consideration/Cost",
      width: 130,
      renderCell: (params) =>
        formatAmount(params.row?.currency, params.row?.consideration),
    },
    {
      field: "total_fee",
      headerName: "Total Fee",
      width: 130,
      renderCell: (params) =>
        formatAmount(params.row?.currency, params.row?.total_fees),
    },
    {
      field: "total_amount",
      headerName: "Total Amount",
      width: 130,
      renderCell: (params) => {
        const rowIsSell =
          (
            params.row?.side ||
            params.row?.type ||
            params.row?.order_type ||
            ""
          ).toLowerCase() === "sell";
        const value = rowIsSell
          ? params.row?.consideration - params.row?.total_fees
          : params.row?.consideration + params.row?.total_fees;
        return formatAmount(params.row?.currency, value);
      },
    },
    {
      field: "brokers",
      headerName: "Broker's Commission",
      width: 130,
      renderCell: (params) =>
        formatAmount(
          params.row?.currency,
          params.row?.fee_breakdown?.broker_commission,
        ),
    },
  ];

  const purificationColumns = [
    {
      field: "purification_amount",
      headerName: "Purification Amount",
      width: 150,
      renderCell: (params) => {
        const value =
          params.row?.purification?.purification_amount ??
          params.row?.purification?.execution_purification_amount;
        return formatAmount(params.row?.currency, value);
      },
    },
    {
      field: "purification_method",
      headerName: "Purification Method",
      width: 160,
      renderCell: (params) => (
        <span style={{ textTransform: "capitalize" }}>
          {params.row?.purification?.purification_method || "-"}
        </span>
      ),
    },
  ];

  const trailingColumns = [
    {
      field: "status",
      headerName: "Status",
      width: 120,
      renderCell: (params) => (
        <div
          style={{
            display: "inline-block",
            padding: "0 8px",
            height: "22px",
            borderRadius: "10px",
            textAlign: "center",
            lineHeight: "22px",
            textTransform: "capitalize",
            ...statusStyle(params.row?.status),
          }}
        >
          {params.row?.status || "Pending"}
        </div>
      ),
    },
    {
      field: "created_at",
      headerName: "Date",
      width: 160,
      renderCell: (params) =>
        params.row?.created_at
          ? new Date(params.row.created_at).toLocaleDateString()
          : "-",
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 200,
      sortable: false,
      renderCell: (params) =>
        isPending(params.row?.status) ? (
          <div className="flex items-center gap-2">
            <CustomButton
              variant="contained"
              customColor="#159AA8"
              children="Approve"
              width="80px"
              height="32px"
              color="#fff"
              fz="12px"
              onClick={() => {
                setSelectedOrder(params.row);
                setShowApprove(true);
              }}
            />
            <CustomButton
              variant="outlined"
              customColor="#fff"
              children="Reject"
              width="80px"
              height="32px"
              borderColor="#B42318"
              color="#B42318"
              fz="12px"
              onClick={() => {
                setSelectedOrder(params.row);
                setShowReject(true);
              }}
            />
          </div>
        ) : canSell(params.row) ? (
          <CustomButton
            variant="contained"
            customColor="#B42318"
            children="Sell for User"
            width="130px"
            height="32px"
            color="#fff"
            fz="12px"
            onClick={() => {
              setSelectedOrder(params.row);
              setShowSellForUser(true);
            }}
          />
        ) : (
          <span className="text-[#475467]">—</span>
        ),
    },
  ];

  // "Dividend History" lists the aggregate dividend events declared per stock
  // (one row per payout run), not the per-user breakdown.
  const dividendColumns = [
    { field: "id", headerName: "ID", width: 70 },
    {
      field: "reference",
      headerName: "Reference",
      width: 180,
      renderCell: (params) => params.row?.reference || "-",
    },
    {
      field: "stock",
      headerName: "Stock",
      width: 200,
      renderCell: (params) => (
        <div className="flex items-center gap-2">
          {params.row?.stock?.logo_url && (
            <img
              src={params.row.stock.logo_url}
              alt={params.row?.stock?.symbol}
              className="w-7 h-7 rounded-full object-contain border border-[#EAECF0]"
            />
          )}
          <div>
            <p className="font-[600]">{params.row?.stock?.symbol || "-"}</p>
            <p className="text-xs text-[#475467]">{params.row?.stock?.name}</p>
          </div>
        </div>
      ),
    },
    {
      field: "dividend_amount",
      headerName: "Dividend Amount",
      width: 150,
      renderCell: (params) =>
        formatAmount(params.row?.currency, params.row?.dividend_amount),
    },
    {
      field: "purification_percentage",
      headerName: "Purification %",
      width: 130,
      renderCell: (params) =>
        params.row?.purification_percentage != null
          ? `${params.row.purification_percentage}%`
          : "-",
    },
    {
      field: "purification_amount",
      headerName: "Purification Amount",
      width: 160,
      renderCell: (params) =>
        formatAmount(params.row?.currency, params.row?.purification_amount),
    },
    {
      field: "net_amount",
      headerName: "Net Amount",
      width: 150,
      renderCell: (params) =>
        formatAmount(params.row?.currency, params.row?.net_amount),
    },
    {
      field: "cut_off_date",
      headerName: "Cut-off Date",
      width: 130,
      renderCell: (params) => params.row?.cut_off_date || "-",
    },
    {
      field: "total_net_volume",
      headerName: "Net Volume",
      width: 120,
      renderCell: (params) =>
        params.row?.total_net_volume != null
          ? Number(params.row.total_net_volume).toLocaleString()
          : "-",
    },
    {
      field: "eligible_users_count",
      headerName: "Eligible Users",
      width: 130,
      renderCell: (params) => params.row?.eligible_users_count ?? "-",
    },
    {
      field: "credited_count",
      headerName: "Credited",
      width: 100,
      renderCell: (params) => params.row?.credited_count ?? "-",
    },
    {
      field: "failed_count",
      headerName: "Failed",
      width: 90,
      renderCell: (params) => params.row?.failed_count ?? "-",
    },
    {
      field: "status",
      headerName: "Status",
      width: 120,
      renderCell: (params) => (
        <div
          style={{
            display: "inline-block",
            padding: "0 8px",
            height: "22px",
            borderRadius: "10px",
            textAlign: "center",
            lineHeight: "22px",
            textTransform: "capitalize",
            ...statusStyle(params.row?.status),
          }}
        >
          {params.row?.status || "-"}
        </div>
      ),
    },
    {
      field: "created_at",
      headerName: "Date",
      width: 160,
      renderCell: (params) =>
        params.row?.created_at
          ? new Date(params.row.created_at).toLocaleDateString()
          : "-",
    },
  ];

  const columns = isDividend
    ? dividendColumns
    : isSell
      ? [...baseColumns, ...purificationColumns, ...trailingColumns]
      : [...baseColumns, ...trailingColumns];

  const dividendCsvHeaders = [
    "ID",
    "Reference",
    "Symbol",
    "Stock",
    "Dividend Amount",
    "Purification %",
    "Purification Amount",
    "Net Amount",
    "Cut-off Date",
    "Net Volume",
    "Eligible Users",
    "Credited",
    "Failed",
    "Status",
    "Date",
  ];

  const dividendCsvRows = orders.map((row) => [
    row?.id,
    row?.reference || "-",
    row?.stock?.symbol || "-",
    row?.stock?.name || "-",
    formatAmount(row?.currency, row?.dividend_amount),
    row?.purification_percentage != null
      ? `${row.purification_percentage}%`
      : "-",
    formatAmount(row?.currency, row?.purification_amount),
    formatAmount(row?.currency, row?.net_amount),
    row?.cut_off_date || "-",
    row?.total_net_volume ?? "-",
    row?.eligible_users_count ?? "-",
    row?.credited_count ?? "-",
    row?.failed_count ?? "-",
    row?.status || "-",
    row?.created_at ? new Date(row.created_at).toLocaleDateString() : "-",
  ]);

  const csvHeaders = [
    "Order ID",
    "User",
    "Symbol",
    "Type",
    "Volume",
    "Price Per Stock",
    "Consideration/Cost",
    "Total Fee",
    "Total Amount",
    "Broker's Commission",
    ...(isSell ? ["Purification Amount", "Purification Method"] : []),
    "Status",
    "Date",
  ];

  const orderCsvRows = orders.map((row) => [
    row?.id,
    row?.user?.name || row?.user_name || row?.user?.email || "-",
    row?.stock?.symbol || row?.symbol || "-",
    row?.side || row?.type || row?.order_type || "-",
    row?.volume ?? row?.quantity ?? "-",
    formatAmount(row?.currency, row?.fee_breakdown?.price),
    formatAmount(row?.currency, row?.consideration),
    formatAmount(row?.currency, row?.total_fees),
    formatAmount(row?.currency, row?.total_fees + row?.consideration),
    formatAmount(row?.currency, row?.fee_breakdown?.broker_commission),
    ...(isSell
      ? [
          formatAmount(
            row?.currency,
            row?.purification?.purification_amount ??
              row?.purification?.execution_purification_amount,
          ),
          row?.purification?.purification_method || "-",
        ]
      : []),
    row?.status || "Pending",
    row?.created_at ? new Date(row.created_at).toLocaleDateString() : "-",
  ]);

  return (
    <Box sx={{ overflowX: "auto", width: "100%" }}>
      <div className="mb-4 w-full overflow-x-auto">
        <div className="w-[max-content] flex items-center border-b border-b-[#EAECF0]">
          {orderTabs.map((tab) => (
            <span
              key={tab.id}
              className={`${
                side === tab.id
                  ? "border-b-2 border-[#128591] text-[#128591] font-[600]"
                  : "text-gray-700"
              } mr-6 text-base pb-2 cursor-pointer`}
              onClick={() => setSide(tab.id)}
            >
              {tab.name}
            </span>
          ))}
        </div>
      </div>

      <div className="flex mb-2 justify-between items-center gap-3">
        <DebouncedSearchInput
          key={side}
          placeholder={
            isDividend ? "Search by stock name" : "Search by user name"
          }
          onSearch={handleSearch}
        />
        <ExportCsvTable
          headers={isDividend ? dividendCsvHeaders : csvHeaders}
          rows={isDividend ? dividendCsvRows : orderCsvRows}
          filename={
            isDividend
              ? "Stock-Dividend-History.csv"
              : `Stock-${isSell ? "Sell" : "Buy"}-Orders.csv`
          }
        />
      </div>

      {loading && orders?.length < 1 ? (
        <Loader />
      ) : (
        <GridTable
          key={`${side}-${searchTerm}`}
          rows={orders}
          pageInfo={pageInfo}
          columns={columns}
          type={isDividend ? "stock-dividend-history" : "stock-orders"}
          noSearch
          noReport
        />
      )}

      <ApproveOrderModal
        open={showApprove}
        order={selectedOrder}
        onClose={() => setShowApprove(false)}
      />
      <RejectOrderModal
        open={showReject}
        order={selectedOrder}
        onClose={() => setShowReject(false)}
      />

      <SellStockForUserModal
        open={showSellForUser}
        order={selectedOrder}
        onClose={() => setShowSellForUser(false)}
      />
    </Box>
  );
}

export default OrdersTable;
